import { SupportedPlans } from 'api/billing/types';

export type BillingFeatures = {
  header: string;
  features: BillingSubFeatures[];
};

enum BillingFeatureGroups {
  CustomerExperience = 'CustomerExperience',
  ProjectPortfolioManagement = 'ProjectPortfolioManagement',
  Templates = 'Templates',
  Files = 'Files',
  TimeCapture = 'TimeCapture',
  Automations = 'Automations',
  Reports = 'Reports',
  Collaboration = 'Collaboration',
  Integration = 'Integration',
  AdminPrivacySecurity = 'AdminPrivacySecurity',
  PartnerContractorCollaboration = 'PartnerContractorCollaboration',
  ResourceManagement = 'ResourceManagement',
  FinancialManagement = 'FinancialManagement',
}

enum BillingSubFeatures {
  // CustomerExperience
  BrandedCustomerPortal = 'BrandedCustomerPortal',
  ProjectPresentation = 'ProjectPresentation',
  MagicLinkLogin = 'MagicLinkLogin',
  CustomerApprovalCollaboration = 'CustomerApprovalCollaboration',
  ActionableEmails = 'ActionableEmails',
  CSATMilestones = 'CSATMilestones',
  // ProjectPortfolioManagement
  ProjectUpdates = 'ProjectUpdates',
  TasksView = 'TasksView',
  Smartfill = 'Smartfill',
  Sprints = 'Sprints',
  KeyEventsInterval = 'KeyEventsInterval',
  // Templates
  ProjectDocumentTemplates = 'ProjectDocumentTemplates',
  FormTemplates = 'FormTemplates',
  EmailTemplates = 'EmailTemplates',
  ProjectUpdateTemplates = 'ProjectUpdateTemplates',
  DynamicTemplates = 'DynamicTemplates',
  // Files
  Attachments = 'Attachments',
  Spaces = 'Spaces',
  Forms = 'Forms',
  // TimeCapture
  TimeTracking = 'TimeTracking',
  Timesheets = 'Timesheets',
  TimesheetApprovals = 'TimesheetApprovals',
  CalendarIntegration = 'CalendarIntegration',
  TimeOff = 'TimeOff',
  // Automations
  CustomAutomations50 = 'CustomAutomations50',
  CustomAutomations200 = 'CustomAutomations200',
  CustomAutomations500 = 'CustomAutomations500',
  CustomAutomationsUnlimited = 'CustomAutomationsUnlimited',
  ProjectBudgetedHoursAlerts = 'ProjectBudgetedHoursAlerts',
  FormsAutomation = 'FormsAutomation',
  // Reports
  CsatReports = 'CsatReports',
  ProjectPerformanceReport = 'ProjectPerformanceReport',
  PeoplePerformanceReport = 'PeoplePerformanceReport',
  ProjectProfitabilityReport = 'ProjectProfitabilityReport',
  PortfolioReport = 'PortfolioReport',
  IntervalIqReport = 'IntervalIqReport',
  OperationsInsightReport = 'OperationsInsightReport',
  CustomReports = 'CustomReports',
  // Collaboration
  TaskCollaboration = 'TaskCollaboration',
  DocumentCollaboration = 'DocumentCollaboration',
  MobileApp = 'MobileApp',
  ProjectCollaboration = 'ProjectCollaboration',
  // Integration
  Zapier = 'Zapier',
  Hubspot = 'Hubspot',
  Jira = 'Jira',
  Slack = 'Slack',
  Salesforce = 'Salesforce',
  Workato = 'Workato',
  Snowflake = 'Snowflake',
  // AdminPrivacySecurity
  PrivacySharing = 'PrivacySharing',
  CustomDomains = 'CustomDomains',
  Api = 'Api',
  CustomEmailDomain = 'CustomEmailDomain',
  RBAC = 'RBAC',
  SAML = 'SAML',
  // PartnerContractorCollaboration
  PartnerPortal = 'PartnerPortal',
  PartnerCSAT = 'PartnerCSAT',
  MetricsPerformanceMonitoring = 'MetricsPerformanceMonitoring',
  PartnerGovernance = 'PartnerGovernance',
  // ResourceManagement
  ResourceAI = 'ResourceAi',
  ResourcePlanning = 'ResourcePlanning',
  AutoAllocation = 'AutoAllocation',
  CapacityManagement = 'CapacityManagement',
  UtilizationTracking = 'UtilizationTracking',
  SoftAllocations = 'SoftAllocations',
  SkillMatrix = 'SkillMatrix',
  // FinancialManagement
  ProjectBudgetTracking = 'ProjectBudgetTracking',
  ProjectBudgetHours = 'ProjectBudgetHours',
  FlexibleBillingOptions = 'FlexibleBillingOptions',
  RateCards = 'RateCards',
  MarginsProfitability = 'MarginsProfitability',
  RevenueRecognition = 'RevenueRecognition',
  Invoicing = 'Invoicing',
  MultiCurrencySupport = 'MultiCurrencySupport',
}

export const billingFeatureList = {
  [SupportedPlans.Essentials]: [
    {
      header: BillingFeatureGroups.CustomerExperience,
      features: [
        BillingSubFeatures.BrandedCustomerPortal,
        BillingSubFeatures.ProjectPresentation,
        BillingSubFeatures.MagicLinkLogin,
        BillingSubFeatures.CustomerApprovalCollaboration,
        BillingSubFeatures.ActionableEmails,
        BillingSubFeatures.CSATMilestones,
      ],
    },
    {
      header: BillingFeatureGroups.ProjectPortfolioManagement,
      features: [
        BillingSubFeatures.ProjectUpdates,
        BillingSubFeatures.TasksView,
      ],
    },
    {
      header: BillingFeatureGroups.Templates,
      features: [
        BillingSubFeatures.ProjectDocumentTemplates,
        BillingSubFeatures.EmailTemplates,
      ],
    },
    {
      header: BillingFeatureGroups.Files,
      features: [BillingSubFeatures.Attachments, BillingSubFeatures.Spaces],
    },
    {
      header: BillingFeatureGroups.TimeCapture,
      features: [
        BillingSubFeatures.TimeTracking,
        BillingSubFeatures.Timesheets,
        BillingSubFeatures.TimesheetApprovals,
      ],
    },
    {
      header: BillingFeatureGroups.Automations,
      features: [BillingSubFeatures.CustomAutomations50],
    },
    {
      header: BillingFeatureGroups.Reports,
      features: [BillingSubFeatures.CsatReports],
    },
    {
      header: BillingFeatureGroups.Collaboration,
      features: [
        BillingSubFeatures.TaskCollaboration,
        BillingSubFeatures.DocumentCollaboration,
        BillingSubFeatures.MobileApp,
        BillingSubFeatures.ProjectCollaboration,
      ],
    },
    {
      header: BillingFeatureGroups.Integration,
      features: [BillingSubFeatures.Zapier],
    },
    {
      header: BillingFeatureGroups.AdminPrivacySecurity,
      features: [BillingSubFeatures.PrivacySharing],
    },
  ],
  [SupportedPlans.Standard]: [
    {
      header: BillingFeatureGroups.ProjectPortfolioManagement,
      features: [BillingSubFeatures.Smartfill, BillingSubFeatures.Sprints],
    },
    {
      header: BillingFeatureGroups.Templates,
      features: [
        BillingSubFeatures.ProjectUpdateTemplates,
        BillingSubFeatures.DynamicTemplates,
        BillingSubFeatures.FormTemplates,
      ],
    },
    {
      header: BillingFeatureGroups.Files,
      features: [BillingSubFeatures.Forms],
    },
    {
      header: BillingFeatureGroups.TimeCapture,
      features: [BillingSubFeatures.CalendarIntegration],
    },
    {
      header: BillingFeatureGroups.Automations,
      features: [BillingSubFeatures.CustomAutomations200],
    },
    {
      header: BillingFeatureGroups.Reports,
      features: [
        BillingSubFeatures.ProjectPerformanceReport,
        BillingSubFeatures.PeoplePerformanceReport,
      ],
    },
    {
      header: BillingFeatureGroups.PartnerContractorCollaboration,
      features: [
        BillingSubFeatures.PartnerPortal,
        BillingSubFeatures.PartnerCSAT,
        BillingSubFeatures.MetricsPerformanceMonitoring,
        BillingSubFeatures.PartnerGovernance,
      ],
    },
    {
      header: BillingFeatureGroups.Integration,
      features: [
        BillingSubFeatures.Hubspot,
        BillingSubFeatures.Jira,
        BillingSubFeatures.Slack,
      ],
    },
    {
      header: BillingFeatureGroups.AdminPrivacySecurity,
      features: [BillingSubFeatures.CustomDomains, BillingSubFeatures.Api],
    },
  ],
  [SupportedPlans['Premium-V2']]: [
    {
      header: BillingFeatureGroups.ProjectPortfolioManagement,
      features: [BillingSubFeatures.KeyEventsInterval],
    },
    {
      header: BillingFeatureGroups.TimeCapture,
      features: [BillingSubFeatures.TimeOff],
    },
    {
      header: BillingFeatureGroups.Automations,
      features: [
        BillingSubFeatures.CustomAutomations500,
        BillingSubFeatures.ProjectBudgetedHoursAlerts,
      ],
    },
    {
      header: BillingFeatureGroups.ResourceManagement,
      features: [
        BillingSubFeatures.ResourceAI,
        BillingSubFeatures.ResourcePlanning,
        BillingSubFeatures.AutoAllocation,
        BillingSubFeatures.CapacityManagement,
        BillingSubFeatures.UtilizationTracking,
      ],
    },
    {
      header: BillingFeatureGroups.FinancialManagement,
      features: [
        BillingSubFeatures.ProjectBudgetTracking,
        BillingSubFeatures.ProjectBudgetHours,
        BillingSubFeatures.FlexibleBillingOptions,
        BillingSubFeatures.RateCards,
        BillingSubFeatures.MarginsProfitability,
        BillingSubFeatures.RevenueRecognition,
        BillingSubFeatures.Invoicing,
      ],
    },
    {
      header: BillingFeatureGroups.Reports,
      features: [
        BillingSubFeatures.ProjectProfitabilityReport,
        BillingSubFeatures.PortfolioReport,
        BillingSubFeatures.IntervalIqReport,
        BillingSubFeatures.OperationsInsightReport,
      ],
    },
    {
      header: BillingFeatureGroups.Integration,
      features: [BillingSubFeatures.Salesforce, BillingSubFeatures.Workato],
    },
    {
      header: BillingFeatureGroups.AdminPrivacySecurity,
      features: [BillingSubFeatures.CustomEmailDomain],
    },
  ],
  [SupportedPlans['Enterprise-V2']]: [
    {
      header: BillingFeatureGroups.Automations,
      features: [
        BillingSubFeatures.CustomAutomationsUnlimited,
        BillingSubFeatures.FormsAutomation,
      ],
    },
    {
      header: BillingFeatureGroups.Reports,
      features: [BillingSubFeatures.CustomReports],
    },
    {
      header: BillingFeatureGroups.ResourceManagement,
      features: [
        BillingSubFeatures.SoftAllocations,
        BillingSubFeatures.SkillMatrix,
      ],
    },
    {
      header: BillingFeatureGroups.FinancialManagement,
      features: [BillingSubFeatures.MultiCurrencySupport],
    },
    {
      header: BillingFeatureGroups.Integration,
      features: [BillingSubFeatures.Snowflake],
    },
    {
      header: BillingFeatureGroups.AdminPrivacySecurity,
      features: [BillingSubFeatures.RBAC, BillingSubFeatures.SAML],
    },
  ],
};
